import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../App";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { useParams, useSearchParams } from "react-router-dom";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";


export default function DomicilePayrolls() {
  const { axiosInstance } = useAuth();

  const { caregiverId } = useParams();

  const [searchParams] = useSearchParams();

  const currentDate = new Date();

  const initialMonth =
    searchParams.get("month") ||
    String(currentDate.getMonth() + 1).padStart(2, "0");

  const initialYear =
    searchParams.get("year") ||
    String(currentDate.getFullYear());

  const [selectedMonth, setSelectedMonth] =
    useState(initialMonth);

  const [selectedYear, setSelectedYear] =
    useState(initialYear);

  const [prestations, setPrestations] = useState([]);

  const [caregiver, setCaregiver] = useState(null);

  const [monthlyAmount, setMonthlyAmount] =
    useState("");

  const [loading, setLoading] =
    useState(true);


  // ============================================================
  // OUTILS
  // ============================================================

  const normalizeId = (value) => {
    if (!value) return "";

    if (typeof value === "string") {
      return value.trim();
    }

    return String(
      value._id ||
      value.id ||
      value.user_id ||
      ""
    ).trim();
  };


  const getFullName = (person) => {
    if (!person) return "—";

    const firstName =
      person.first_name ||
      person.prenom ||
      "";

    const lastName =
      person.last_name ||
      person.nom ||
      "";

    return `${firstName} ${lastName}`.trim() || "—";
  };


  const getRoleLabel = (person) => {
    if (!person) return "Soignant";

    if (person.role === "nurse") {
      return "Infirmier(ère)";
    }

    if (person.role === "caregiver") {
      return "Aide-soignant(e)";
    }

    return (
      person.care_role ||
      "Soignant"
    );
  };


  const monthNames = {
    "01": "Janvier",
    "02": "Février",
    "03": "Mars",
    "04": "Avril",
    "05": "Mai",
    "06": "Juin",
    "07": "Juillet",
    "08": "Août",
    "09": "Septembre",
    "10": "Octobre",
    "11": "Novembre",
    "12": "Décembre"
  };


  // ============================================================
  // CHARGEMENT
  // ============================================================

  const fetchData = async () => {
    if (!caregiverId) {
      toast.error(
        "Identifiant du soignant manquant."
      );

      return;
    }

    setLoading(true);

    try {

      // ----------------------------------------------------------
      // PRESTATIONS À DOMICILE
      // ----------------------------------------------------------

      const prestationsResponse =
        await axiosInstance.get(
          "/prestations-domicile",
          {
            params: {
              soignant_id: caregiverId
            }
          }
        );

      const prestationList =
        Array.isArray(prestationsResponse.data)
          ? prestationsResponse.data
          : prestationsResponse.data?.prestations || [];

      setPrestations(prestationList);


      // ----------------------------------------------------------
      // SOIGNANT
      // ----------------------------------------------------------

      let selectedCaregiver = null;

      const prestationWithCaregiver =
        prestationList.find(
          (item) => item.soignant
        );

      if (prestationWithCaregiver?.soignant) {
        selectedCaregiver =
          prestationWithCaregiver.soignant;
      }


      // Si aucune prestation ne donne les informations du soignant,
      // on le recherche dans /users.

      if (!selectedCaregiver) {

        const usersResponse =
          await axiosInstance.get(
            "/users"
          );

        const userList =
          Array.isArray(usersResponse.data)
            ? usersResponse.data
            : usersResponse.data?.users || [];

        selectedCaregiver =
          userList.find(
            (item) =>
              normalizeId(item) ===
              normalizeId(caregiverId)
          );
      }

      setCaregiver(
        selectedCaregiver || null
      );


      // ----------------------------------------------------------
      // MONTANT DU MOIS
      // ----------------------------------------------------------

      try {

        const amountResponse =
          await axiosInstance.get(
            "/montants-mensuels-soignants",
            {
              params: {
                soignant_id: caregiverId,
                mois: Number(selectedMonth),
                annee: Number(selectedYear)
              }
            }
          );

        setMonthlyAmount(
          amountResponse.data?.montant ?? ""
        );

      } catch (amountError) {

        console.error(
          "Erreur montant mensuel :",
          amountError.response?.data ||
          amountError
        );

        setMonthlyAmount("");
      }

    } catch (error) {

      console.error(
        "Erreur chargement fiche domicile :",
        error.response?.data || error
      );

      toast.error(
        error.response?.data?.detail ||
        "Impossible de charger les prestations."
      );

    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchData();
  }, [
    caregiverId,
    selectedMonth,
    selectedYear
  ]);


  // ============================================================
  // FILTRAGE DU MOIS
  // ============================================================

  const filteredPrestations =
    useMemo(() => {

      return prestations
        .filter((prestation) => {

          if (!prestation.date) {
            return false;
          }

          const prestationDate =
            new Date(prestation.date);

          const sameMonth =
            prestationDate.getMonth() + 1 ===
            Number(selectedMonth);

          const sameYear =
            prestationDate.getFullYear() ===
            Number(selectedYear);

          return (
            sameMonth &&
            sameYear
          );
        })
        .sort(
          (a, b) =>
            new Date(a.date) -
            new Date(b.date)
        );

    }, [
      prestations,
      selectedMonth,
      selectedYear
    ]);


  // ============================================================
  // STATISTIQUES
  // ============================================================

  const totalPassages =
    filteredPrestations.reduce(
      (total, prestation) =>
        total +
        (
          Array.isArray(prestation.passages)
            ? prestation.passages.length
            : 0
        ),
      0
    );


  const uniquePatients =
    new Set(
      filteredPrestations
        .map((prestation) => {

          const patient =
            prestation.patient ||
            prestation.patient_id;

          if (
            patient &&
            typeof patient === "object"
          ) {
            return normalizeId(patient);
          }

          return String(
            patient || ""
          );
        })
        .filter(Boolean)
    ).size;


  // ============================================================
  // PDF
  // ============================================================

  const exportPdf = () => {

    if (
      filteredPrestations.length === 0
    ) {
      toast.error(
        "Aucune prestation pour cette période."
      );

      return;
    }


    if (
      monthlyAmount === "" ||
      monthlyAmount === null
    ) {
      toast.error(
        "Aucun montant mensuel n'a été enregistré."
      );

      return;
    }


    const doc =
      new jsPDF(
        "portrait",
        "pt",
        "a4"
      );

    const pageWidth =
      doc.internal.pageSize.getWidth();


    // ==========================================================
    // LOGO
    // ==========================================================

    const logo =
      new Image();

    logo.src =
      "/logo.jpeg";


    logo.onload = () => {

      doc.addImage(
        logo,
        "JPEG",
        40,
        30,
        90,
        45
      );


      // ========================================================
      // ENTREPRISE
      // ========================================================

      doc.setFont(
        "Helvetica",
        "bold"
      );

      doc.setFontSize(16);

      doc.text(
        "SANA CARE DIAMOND",
        pageWidth - 40,
        50,
        {
          align: "right"
        }
      );


      doc.setFontSize(9);

      doc.setFont(
        "Helvetica",
        "normal"
      );

      doc.text(
        "Soins à domicile",
        pageWidth - 40,
        67,
        {
          align: "right"
        }
      );

      doc.text(
        "info@sanacare-be.com",
        pageWidth - 40,
        82,
        {
          align: "right"
        }
      );

      doc.text(
        "Avenue louise 200, 1050 Bruxelles",
        pageWidth - 40,
        100,
        {
          align: "right"
        }
      );


      doc.setLineWidth(1);

      doc.line(
        40,
        115,
        pageWidth - 40,
        115
      );


      // ========================================================
      // TITRE
      // ========================================================

      doc.setFont(
        "Helvetica",
        "bold"
      );

      doc.setFontSize(18);

      doc.text(
        "FICHE DE RÉMUNÉRATION",
        pageWidth / 2,
        150,
        {
          align: "center"
        }
      );


      doc.setFontSize(12);

      doc.text(
        "PRESTATIONS À DOMICILE",
        pageWidth / 2,
        170,
        {
          align: "center"
        }
      );


      doc.setFontSize(11);

      doc.setFont(
        "Helvetica",
        "normal"
      );

      doc.text(
        `${monthNames[selectedMonth]} ${selectedYear}`,
        pageWidth / 2,
        190,
        {
          align: "center"
        }
      );


      // ========================================================
      // SOIGNANT
      // ========================================================

      doc.setFont(
        "Helvetica",
        "bold"
      );

      doc.setFontSize(12);

      doc.text(
        "SOIGNANT",
        40,
        225
      );


      doc.setFont(
        "Helvetica",
        "normal"
      );

      doc.setFontSize(10);

      doc.text(
        `Nom : ${getFullName(caregiver)}`,
        40,
        245
      );

      doc.text(
        `Fonction : ${getRoleLabel(caregiver)}`,
        40,
        260
      );

      doc.text(
        `Email : ${caregiver?.email || "—"}`,
        40,
        275
      );

      doc.text(
        `Téléphone : ${
          caregiver?.phone ||
          caregiver?.telephone ||
          "—"
        }`,
        40,
        290
      );


      doc.text(
        `Adresse : ${
          caregiver?.address ||
          caregiver?.adresse ||
          "—"
        }`,
        40,
        305
      );


      // ========================================================
      // RÉCAPITULATIF
      // ========================================================

      doc.setFont(
        "Helvetica",
        "bold"
      );

      doc.text(
        "RÉCAPITULATIF DU MOIS",
        330,
        225
      );


      doc.setFont(
        "Helvetica",
        "normal"
      );

      doc.text(
        `Prestations : ${filteredPrestations.length}`,
        330,
        245
      );

      doc.text(
        `Passages : ${totalPassages}`,
        330,
        260
      );

      doc.text(
        `Patients : ${uniquePatients}`,
        330,
        275
      );


      doc.setFont(
        "Helvetica",
        "bold"
      );

      doc.setFontSize(12);

      doc.text(
        `Montant : ${Number(
          monthlyAmount
        ).toFixed(2)} EUR`,
        330,
        300
      );


      doc.line(
        40,
        325,
        pageWidth - 40,
        325
      );


      // ========================================================
      // TABLEAU
      // ========================================================

      const columns = [
        "Date",
        "Patient",
        "Matin",
        "Midi",
        "Soir",
        "Notes"
      ];


      const rows =
        filteredPrestations.map(
          (prestation) => {

            const patient =
              prestation.patient ||
              prestation.patient_id;

            let patientName =
              "Patient inconnu";

            if (
              patient &&
              typeof patient === "object"
            ) {

              const prenom =
                patient.prenom ||
                patient.first_name ||
                "";

              const nom =
                patient.nom ||
                patient.last_name ||
                "";

              patientName =
                `${prenom} ${nom}`.trim() ||
                "Patient";
            }


            const passages =
              Array.isArray(
                prestation.passages
              )
                ? prestation.passages
                : [];


            return [
              new Date(
                prestation.date
              ).toLocaleDateString(
                "fr-BE"
              ),

              patientName,

              passages.includes("matin")
                ? "X"
                : "—",

              passages.includes("midi")
                ? "X"
                : "—",

              passages.includes("soir")
                ? "X"
                : "—",

              prestation.notes ||
              "—"
            ];
          }
        );


      autoTable(
        doc,
        {
          startY: 345,

          head: [
            columns
          ],

          body: rows,

          styles: {
            fontSize: 8,
            cellPadding: 4,
            valign: "middle"
          },

          headStyles: {
            fillColor: [212, 235, 235],
            textColor: [63, 111, 114],
            fontSize: 9,
            halign: "center"
          },

          columnStyles: {
            0: {
              cellWidth: 65
            },

            1: {
              cellWidth: 110
            },

            2: {
              cellWidth: 45,
              halign: "center"
            },

            3: {
              cellWidth: 45,
              halign: "center"
            },

            4: {
              cellWidth: 45,
              halign: "center"
            }
          },

          margin: {
            left: 40,
            right: 40
          }
        }
      );


      // ========================================================
      // TOTAL
      // ========================================================

      let finalY =
        doc.lastAutoTable.finalY + 30;


      if (finalY > 760) {

        doc.addPage();

        finalY = 70;
      }


      doc.setFont(
        "Helvetica",
        "bold"
      );

      doc.setFontSize(11);

      doc.text(
        `Nombre total de prestations : ${filteredPrestations.length}`,
        pageWidth - 40,
        finalY,
        {
          align: "right"
        }
      );


      doc.text(
        `Nombre total de passages : ${totalPassages}`,
        pageWidth - 40,
        finalY + 18,
        {
          align: "right"
        }
      );


      doc.setFontSize(15);

      doc.text(
        `TOTAL À PAYER : ${Number(
          monthlyAmount
        ).toFixed(2)} EUR`,
        pageWidth - 40,
        finalY + 48,
        {
          align: "right"
        }
      );


      doc.setFont(
        "Helvetica",
        "italic"
      );

      doc.setFontSize(8);

      doc.text(
        "Document généré automatiquement par Sana Care Diamond.",
        40,
        finalY + 85
      );


      // ========================================================
      // NOM FICHIER
      // ========================================================

      const caregiverName =
        getFullName(caregiver)
          .replace(/\s+/g, "_")
          .replace(
            /[^a-zA-Z0-9_-]/g,
            ""
          );


      doc.save(
        `fiche_domicile_${caregiverName}_${selectedYear}_${selectedMonth}.pdf`
      );
    };


    // Si le logo ne charge pas,
    // on évite de bloquer silencieusement.

    logo.onerror = () => {
      toast.error(
        "Impossible de charger le logo Sana Care."
      );
    };
  };


  // ============================================================
  // AFFICHAGE
  // ============================================================

  if (loading) {
    return (
      <Layout title="Fiche de rémunération domicile">
        <div className="p-8 text-center text-amber-800">
          Chargement de la fiche…
        </div>
      </Layout>
    );
  }


  return (
    <Layout title="Fiche de rémunération domicile">

      <div className="space-y-6">

        {/* TITRE */}

        <div>
          <h1 className="text-2xl font-bold text-[#3F6F72]">
            Fiche de rémunération — Prestations à domicile
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            {getFullName(caregiver)}
          </p>
        </div>


        {/* FILTRES */}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          <div>
            <label className="text-sm font-medium">
              Mois
            </label>

            <select
              value={selectedMonth}
              onChange={(e) =>
                setSelectedMonth(
                  e.target.value
                )
              }
              className="mt-1 w-full rounded-md border border-amber-200 bg-white px-3 py-2"
            >

              {Object.entries(
                monthNames
              ).map(
                ([value, label]) => (
                  <option
                    key={value}
                    value={value}
                  >
                    {label}
                  </option>
                )
              )}

            </select>
          </div>


          <div>
            <label className="text-sm font-medium">
              Année
            </label>

            <input
              type="number"
              value={selectedYear}
              onChange={(e) =>
                setSelectedYear(
                  e.target.value
                )
              }
              className="mt-1 w-full rounded-md border border-amber-200 bg-white px-3 py-2"
            />
          </div>


          <div className="flex items-end">

            <Button
              onClick={exportPdf}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
            >
              📄 Exporter la fiche en PDF
            </Button>

          </div>

        </div>


        {/* RÉSUMÉ */}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

          <div className="rounded-xl border border-[#D4EBEB] bg-white p-4">
            <p className="text-sm text-gray-500">
              Prestations
            </p>

            <p className="text-2xl font-bold text-[#3F6F72]">
              {filteredPrestations.length}
            </p>
          </div>


          <div className="rounded-xl border border-amber-200 bg-white p-4">
            <p className="text-sm text-gray-500">
              Passages
            </p>

            <p className="text-2xl font-bold text-amber-900">
              {totalPassages}
            </p>
          </div>


          <div className="rounded-xl border border-amber-200 bg-white p-4">
            <p className="text-sm text-gray-500">
              Patients
            </p>

            <p className="text-2xl font-bold text-amber-900">
              {uniquePatients}
            </p>
          </div>


          <div className="rounded-xl border border-[#D4EBEB] bg-[#F1F8F8] p-4">
            <p className="text-sm text-gray-500">
              Montant du mois
            </p>

            <p className="text-2xl font-bold text-[#3F6F72]">
              {monthlyAmount !== ""
                ? `${Number(monthlyAmount).toFixed(2)} €`
                : "Non défini"}
            </p>
          </div>

        </div>


        {/* TABLEAU */}

        <div className="overflow-x-auto rounded-xl border border-amber-200 bg-white">

          <table className="w-full border-collapse text-sm">

            <thead className="bg-[#D4EBEB]">

              <tr>

                <th className="p-3 text-left">
                  Date
                </th>

                <th className="p-3 text-left">
                  Patient
                </th>

                <th className="p-3 text-center">
                  Matin
                </th>

                <th className="p-3 text-center">
                  Midi
                </th>

                <th className="p-3 text-center">
                  Soir
                </th>

                <th className="p-3 text-left">
                  Notes
                </th>

              </tr>

            </thead>


            <tbody>

              {filteredPrestations.map(
                (prestation) => {

                  const patient =
                    prestation.patient ||
                    prestation.patient_id;

                  const patientName =
                    patient &&
                    typeof patient ===
                      "object"
                      ? getFullName(
                          patient
                        )
                      : "Patient inconnu";


                  const passages =
                    Array.isArray(
                      prestation.passages
                    )
                      ? prestation.passages
                      : [];


                  return (
                    <tr
                      key={
                        prestation._id
                      }
                      className="border-t hover:bg-[#F1F8F8]"
                    >

                      <td className="p-3">
                        {new Date(
                          prestation.date
                        ).toLocaleDateString(
                          "fr-BE"
                        )}
                      </td>


                      <td className="p-3 font-medium">
                        {patientName}
                      </td>


                      <td className="p-3 text-center">
                        {passages.includes(
                          "matin"
                        )
                          ? "✓"
                          : "—"}
                      </td>


                      <td className="p-3 text-center">
                        {passages.includes(
                          "midi"
                        )
                          ? "✓"
                          : "—"}
                      </td>


                      <td className="p-3 text-center">
                        {passages.includes(
                          "soir"
                        )
                          ? "✓"
                          : "—"}
                      </td>


                      <td className="p-3">
                        {prestation.notes ||
                          "—"}
                      </td>

                    </tr>
                  );
                }
              )}

            </tbody>

          </table>

        </div>

      </div>

    </Layout>
  );
}